import * as THREE from 'three';
import type { MapDefinition, MapPath } from '../../../shared/game/world-types.js';
import { terrainHeight } from '../../../shared/game/terrain.js';
import { ROAD_SURFACE_LIFT, terrainRoadGeometry } from './terrain-road.js';

/** Beyond the nominal width the surface keeps fading into the ground for this many metres on each side. */
export const ROAD_FADE = 2.8;

const KIND_INDEX = { trail: 0, road: 1, cave: 2 } as const;

/**
 * Road fragments decide, per pixel, how much dirt, rubble and rut there is.
 * The edge wanders in and out along its length, breaks into patches, and fades to transparent so the
 * grass, sand or scree underneath shows through. The centre line (and so collision/navigation) is unchanged.
 */
export function createRoadSurface(path: MapPath, seed: number, map?: MapDefinition): THREE.Mesh {
  const curve = new THREE.CatmullRomCurve3(path.points.map(p => new THREE.Vector3(p.x, 0, p.z)), false, 'centripetal');
  const step = map?.terrain ? Math.max(.35, (map.terrain.cellSize ?? .5) * .65) : 1.5;
  const count = Math.max(12, Math.ceil(curve.getLength() / step)), half = path.width / 2 + ROAD_FADE;
  const crossCount = map?.terrain ? Math.ceil(half * 2 / step) : 1, stride = crossCount + 1;
  const positions: number[] = [], normals: number[] = [], across: number[] = [], along: number[] = [], indices: number[] = [];
  let travelled = 0, previous = curve.getPoint(0);
  if (!map?.terrain) for (let i = 0; i <= count; i++) {
    const t = i / count, p = curve.getPoint(t), tangent = curve.getTangent(t), side = new THREE.Vector3(-tangent.z, 0, tangent.x).normalize();
    travelled += p.distanceTo(previous); previous = p;
    for (let j = 0; j <= crossCount; j++) {
      const offset = -half + half * 2 * j / crossCount, x = p.x + side.x * offset, z = p.z + side.z * offset;
      positions.push(x, ROAD_SURFACE_LIFT + (map ? terrainHeight(map, x, z) : 0), z); normals.push(0, 1, 0);
      across.push(offset); along.push(travelled);
    }
    if (i < count) for (let j = 0; j < crossCount; j++) { const n = i * stride + j; indices.push(n, n + 1, n + stride, n + 1, n + stride + 1, n + stride); }
  }
  const geometry = map?.terrain ? terrainRoadGeometry(path, curve, map, ROAD_FADE) : new THREE.BufferGeometry();
  if (!map?.terrain) {
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geometry.setAttribute('across', new THREE.Float32BufferAttribute(across, 1));
    geometry.setAttribute('along', new THREE.Float32BufferAttribute(along, 1));
    geometry.setIndex(indices);
  }

  const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const kind = KIND_INDEX[path.kind], nominal = (path.width / 2).toFixed(3), offset = (seed % 97).toFixed(1);
  const length = (geometry.userData.roadLength ?? travelled) as number;
  const endFade = Math.min(length * .35, Math.max(4, path.width * .8));
  material.customProgramCacheKey = () => `road:${kind}:${nominal}:${offset}:${path.rutted ? 1 : 0}:${length}:${endFade}`;
  material.onBeforeCompile = shader => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float across; attribute float along; varying float vAcross; varying float vAlong;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAcross = across; vAlong = along;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        varying float vAcross; varying float vAlong;
        float roadHeight = 0.; // stone relief in metres, turned into a lit bump after the normal is known
        float rh(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float rn(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.-2.*f);
          return mix(mix(rh(i), rh(i+vec2(1.,0.)), f.x), mix(rh(i+vec2(0.,1.)), rh(i+vec2(1.,1.)), f.x), f.y); }
        float rf(vec2 p){ float v = 0., a = .5; for (int i = 0; i < 4; i++) { v += a*rn(p); p = mat2(.8,.6,-.6,.8)*p*2.03 + 7.1; a *= .5; } return v; }`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        {
          // Screen-space bump mapping from the stone height field (same construction as Three's bump map).
          vec2 dH = vec2(dFdx(roadHeight), dFdy(roadHeight));
          vec3 sigmaX = dFdx(-vViewPosition), sigmaY = dFdy(-vViewPosition);
          vec3 r1 = cross(sigmaY, normal), r2 = cross(normal, sigmaX);
          float det = dot(sigmaX, r1);
          normal = normalize(abs(det) * normal - sign(det) * (dH.x * r1 + dH.y * r2));
        }`)
      .replace('#include <color_fragment>', `#include <color_fragment>
        {
          vec2 q = vec2(vAlong, vAcross);
          float kind = ${kind}.;
          float nominal = ${nominal};
          // The verge breathes: widest where the wheels turned, pinched where the grass pushed in.
          float flow = rf(vec2(vAlong * .045 + ${offset}, 3.3)) - .5, flow2 = rf(vec2(vAlong * .16, 9.1 + ${offset})) - .5;
          float edge = nominal * (1.2 + flow * .4 + flow2 * .16);
          float drift = 0.; // the centre stays on the shared curve so verges, plants and stones line up
          float d = abs(vAcross - drift) - edge;
          // Ragged, patchy fade: noise moves the threshold so the dirt frays into the surroundings.
          float fray = (rf(q * vec2(.55, .75) + ${offset}) - .5) * 2.6 + (rn(q * 2.6) - .5) * .9;
          // Crisp, ragged verge (a hard noisy edge reads like the faceted ground), plus loose islands of dirt beyond it.
          float body = 1. - smoothstep(-.12, .12, d + fray);
          float islands = step(.64, rn(q * .85 + 3.)) * (1. - smoothstep(0., ${ROAD_FADE.toFixed(2)}, d));
          body = max(body, islands * .9);
          // Wear disappears along the final metres, before the mesh cap can become visible.
          float endDistance = min(vAlong, ${length.toFixed(5)} - vAlong);
          float endWear = smoothstep(0., ${endFade.toFixed(5)}, endDistance);
          float endPatch = smoothstep(.08, .7, endWear + (rf(q * .7 + 23.) - .5) * .35);
          body *= endWear * endPatch;
          float worn = 1. - smoothstep(-nominal * .9, 0., d + fray * .5);
          // Soil: two earth tones in broad drifts, finer speckle on top.
          vec3 dirtA = kind > 1.5 ? vec3(.14,.13,.13) : vec3(.33,.25,.14), dirtB = kind > 1.5 ? vec3(.09,.09,.10) : vec3(.21,.16,.09);
          float broad = rf(q * vec2(.18, .26) + 4.), speck = rn(q * 5.5);
          // Tones change in distinct patches rather than smooth gradients, matching the flat-shaded terrain.
          vec3 soil = mix(dirtA, dirtB, smoothstep(.5, .54, broad));
          soil *= .96 + step(.72, speck) * .1 - step(speck, .18) * .08;
          // Wheel ruts in continuous soil.
          float rutLine = abs(abs(vAcross - drift) - nominal * .42);
          float ruts = kind > .5 && kind < 1.5 ? (1. - smoothstep(.0, .38, rutLine)) * worn * (.55 + .45 * rn(vec2(vAlong * .6, 2.)) ) : 0.;
          soil = mix(soil, soil * .62, ruts * .55);
          // Rubble: voronoi pebbles, thicker toward the edges and over the whole cave floor.
          vec2 cell = q * 1.5; vec2 ci = floor(cell), cf = fract(cell);
          float nearest = 9.; vec2 nearestId = vec2(0.), nearestOffset = vec2(0.);
          for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {
            vec2 g = vec2(float(x), float(y)); vec2 id = ci + g;
            vec2 centre = g + vec2(rh(id), rh(id + 19.7)) * .8 + .1;
            float dist = length(cf - centre);
            if (dist < nearest) { nearest = dist; nearestId = id; nearestOffset = cf - centre; }
          }
          // Each stone gets its own outline: rotated, squashed into an oval or slab, with two to four soft lobes.
          float spin = rh(nearestId + 5.2) * 6.2832, aspect = .45 + rh(nearestId + 6.4) * .55;
          vec2 o = mat2(cos(spin), sin(spin), -sin(spin), cos(spin)) * nearestOffset; o.y /= aspect;
          float lobes = 1. + .16 * sin(atan(o.y, o.x) * (2. + floor(rh(nearestId + 7.7) * 3.)) + rh(nearestId + 2.2) * 6.2832);
          nearest = length(o) * lobes;
          float edgeness = smoothstep(.0, 1., (abs(vAcross - drift) - nominal * .45) / (nominal * .75 + .01));
          float density = kind > 1.5 ? .8 : .3 + edgeness * .45 + (1. - worn) * .15;
          float isStone = step(rh(nearestId + 3.1), density);
          // Mostly small stones with the odd larger one; the biggest is smaller than before.
          float radius = .12 + pow(rh(nearestId + 8.8), 1.7) * .26;
          float stone = isStone * (1. - smoothstep(radius * .9, radius, nearest)) * .85;
          float lit = step(nearest, radius * .55);
          // Each stone is a low dome standing proud of the soil, so the sun models it and the far side falls in shade.
          // Eased edge (rounded shoulders, no dark rim); the profile varies from flat slab to tall rounded cobble.
          float dome = pow(smoothstep(radius, radius * .15, nearest), .6 + rh(nearestId + 12.9) * 1.2);
          float tall = .35 + rh(nearestId + 11.3) * 1.15;
          roadHeight = isStone * dome * radius * .6 * tall + (1. - isStone) * speck * .012;
          ${path.rutted ? `
          // Rounded wheel channels catch the same light as stones.
          float wheel = exp(-pow((abs(vAcross) - nominal * .42) / .24, 2.));
          roadHeight -= wheel * .095 * (.8 + .2 * rn(vec2(vAlong * .7, 4.)));
          ` : ''}
          // Stones are the local soil, slightly paler and greyer, with only a gentle domed highlight — no dark rim.
          vec3 rock = mix(soil * vec3(1.05,1.02,1.0), vec3(.40,.35,.29), .45 + rh(nearestId + 1.3) * .2) * (.95 + lit * .1);
          vec3 road = mix(soil, rock, stone);
          // Sun-baked patches and damp dark patches break up the tone further.
          road *= .93 + step(.55, rf(q * .35 + 11.)) * .1;
          diffuseColor.rgb = road;
          diffuseColor.a = body * (kind > 1.5 ? .97 : .95);
          if (diffuseColor.a < .01) discard;
        }`);
  };
  const mesh = new THREE.Mesh(geometry, material); mesh.receiveShadow = true; mesh.renderOrder = 1; return mesh;
}

/** Cobbles, kerb stones and clods strewn along the road edge, interrupting the line of the surface. */
export function roadEdgeSpots(path: MapPath, random: () => number): { x: number; z: number; scale: number }[] {
  const curve = new THREE.CatmullRomCurve3(path.points.map(p => new THREE.Vector3(p.x, 0, p.z)), false, 'centripetal');
  const spots: { x: number; z: number; scale: number }[] = [], length = curve.getLength(), count = Math.ceil(length / 1.6);
  for (let i = 0; i < count; i++) {
    const t = i / count, p = curve.getPoint(t), tangent = curve.getTangent(t), side = new THREE.Vector3(-tangent.z, 0, tangent.x).normalize();
    for (let n = 0; n < 2; n++) {
      if (random() < .35) continue;
      const offset = (random() < .5 ? -1 : 1) * (path.width / 2 + (random() - .35) * 2.2);
      spots.push({ x: p.x + side.x * offset, z: p.z + side.z * offset, scale: .1 + Math.pow(random(), 2) * .55 });
    }
  }
  return spots;
}
